import { buildFixtureSlug } from "@/lib/entities";
import { uiText } from "@/lib/i18n/uiText";
import { seoFor } from "@/lib/i18n/keywordPools";
import { createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import TrackRecord from "@/pages/TrackRecord";
import { MainLayout } from "@/components/layout/MainLayout";
import { getPublicTrackRecord } from "@/lib/publicPicks.functions";

const trackQuery = queryOptions({ queryKey: ["public-track-record"], queryFn: () => getPublicTrackRecord(), staleTime: 10 * 60_000 });

const TITLE = "Prediction Track Record: Verified Results | Statlign";
const DESC = "Every past prediction, evaluated against the real result. Filter by league, market or confidence — the wins and the misses, all on record.";

export const Route = createFileRoute("/track-record")({
  loader: ({ context }) => context.queryClient.ensureQueryData(trackQuery),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "keywords", content: seoFor({ topics: ["trackRecord"] }).meta },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
  }),
  component: TrackRecordRoute,
});

function TrackRecordRoute() {
  const { data } = useSuspenseQuery(trackQuery);
  const total = data.won + data.lost;
  return (
    <MainLayout>
      <TrackRecord />
      <section aria-labelledby="graded-summary" className="mx-auto mt-8 max-w-5xl space-y-4 px-4 sm:px-6">
        <h2 id="graded-summary" className="text-xl font-semibold">{uiText("Graded results summary")}</h2>
        <p className="text-muted-foreground">
          {total > 0
            ? uiText("Win rate {{v0}}% across {{v1}} graded picks — {{v2}} won, {{v3}} lost.", { v0: data.winRate, v1: total.toLocaleString("en"), v2: data.won.toLocaleString("en"), v3: data.lost.toLocaleString("en") })
            : uiText("Graded results will appear here as matches settle.")}
        </p>
        {data.recent.length > 0 && (
          <ul className="space-y-2 sm:hidden" aria-label={uiText("Latest graded predictions")}>
            {data.recent.map((p) => (
              <li key={p.id} className="rounded-lg border border-border bg-card p-3 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium"><a href={`/fixtures/${buildFixtureSlug(p.team_home, p.team_away, p.kickoff_time)}`} className="hover:underline">{p.team_home}  {uiText("vs")} {p.team_away}</a></p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(p.kickoff_time).toISOString().slice(0, 10)}{p.league ? ` · ${p.league}` : ""}
                    </p>
                  </div>
                  <span className={`shrink-0 font-semibold ${p.was_correct ? "text-primary" : "text-destructive"}`}>{p.was_correct ? uiText("Won") : uiText("Lost")}</span>
                </div>
                <p className="mt-1 text-muted-foreground">{uiText(p.market_type)}: {uiText(p.predicted_outcome)} · {p.odds?.toFixed(2) ?? "—"}</p>
              </li>
            ))}
          </ul>
        )}
        {data.recent.length > 0 && (
          <div className="hidden overflow-x-auto rounded-lg border border-border sm:block">
            <table className="w-full text-sm">
              <caption className="sr-only">{uiText("Latest graded predictions")}</caption>
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th scope="col" className="p-2">{uiText("Date")}</th>
                  <th scope="col" className="p-2">{uiText("Match")}</th>
                  <th scope="col" className="p-2">{uiText("Pick")}</th>
                  <th scope="col" className="p-2">{uiText("Odds")}</th>
                  <th scope="col" className="p-2">{uiText("Result")}</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="p-2 whitespace-nowrap">{new Date(p.kickoff_time).toISOString().slice(0, 10)}</td>
                    <td className="p-2"><a href={`/fixtures/${buildFixtureSlug(p.team_home, p.team_away, p.kickoff_time)}`} className="hover:underline">{p.team_home}  {uiText("vs")} {p.team_away}</a>{p.league ? <span className="block text-xs text-muted-foreground">{p.league}</span> : null}</td>
                    <td className="p-2">{uiText(p.market_type)}: {uiText(p.predicted_outcome)}</td>
                    <td className="p-2">{p.odds?.toFixed(2) ?? "—"}</td>
                    <td className={`p-2 font-medium ${p.was_correct ? "text-primary" : "text-destructive"}`}>{p.was_correct ? uiText("Won") : uiText("Lost")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </MainLayout>
  );
}
